<template>
  <div class="bc-spec-detail">
    <div class="bc-spec-detail__inner" v-if="goods.id">
      <a class="bc-spec-detail__back" href="javascript:;" @click.prevent="goMall">
        ← {{ $T('观众中心返回商城') }}
      </a>

      <div class="bc-spec-detail__main">
        <div class="bc-spec-detail__gallery">
          <img
            class="bc-spec-detail__pic"
            :src="currentPic"
            :alt="goodsTitle"
          />
          <div v-if="pics.length > 1" class="bc-spec-detail__thumbs">
            <button
              v-for="(pic, idx) in pics"
              :key="idx"
              type="button"
              class="bc-spec-detail__thumb"
              :class="{ 'is-active': pic === currentPic }"
              @click="currentPic = pic"
            >
              <img :src="pic" alt="" />
            </button>
          </div>
        </div>

        <div class="bc-spec-detail__info">
          <h1 class="bc-spec-detail__title">{{ goodsTitle }}</h1>
          <p v-if="goodsDesc" class="bc-spec-detail__desc">{{ goodsDesc }}</p>
          <p class="bc-spec-detail__price">{{ money(displayPrice) }}</p>

          <div
            v-for="tree in skuTree"
            :key="tree.k_s"
            class="bc-spec-detail__sku-group"
          >
            <div class="bc-spec-detail__sku-label">{{ skuFieldLabel(tree) }}</div>
            <div class="bc-spec-detail__sku-opts">
              <button
                v-for="attr in tree.v"
                :key="attr.id"
                type="button"
                class="bc-spec-detail__sku-opt"
                :class="{ 'is-active': selected[tree.k_s] == attr.id }"
                @click="selectAttr(tree.k_s, attr)"
              >
                {{ skuAttrLabel(attr) }}
              </button>
            </div>
          </div>

          <div class="bc-spec-detail__qty">
            <span class="bc-spec-detail__sku-label">{{ $T('观众中心数量') }}</span>
            <div class="bc-spec-detail__qty-ctrl">
              <button type="button" @click="changeCount(-1)">−</button>
              <input v-model.number="count" type="number" min="1" @change="normalizeCount" />
              <button type="button" @click="changeCount(1)">+</button>
            </div>
          </div>

          <button
            type="button"
            class="bc-spec-detail__buy"
            :disabled="buying"
            @click="buyNow"
          >
            {{ $T('观众中心立即购买') }}
          </button>
        </div>
      </div>

      <div v-if="goodsContent" class="bc-spec-detail__content">
        <h2>{{ $T('观众中心商品详情') }}</h2>
        <div class="bc-spec-detail__html" v-html="goodsContent"></div>
      </div>
    </div>
    <div v-else class="bc-spec-detail__empty">{{ $T('观众中心加载中') }}</div>

    <LoginDialog
      :show="loginShow"
      @loginSuccess="loginSuccess"
      @loginCancel="loginCancel"
    />
  </div>
</template>

<script>
import { Toast } from 'vant';
import LoginDialog from '@/components/LoginDialog.vue';
import { spectatorMoney } from '@/utils/spectatorMoney';

const BUY_KEY = 'spectator_buy_goods_items';

export default {
  name: 'SpectatorGoodsDetail',
  components: { LoginDialog },
  data() {
    return {
      goods: {},
      skuTree: [],
      skuList: [],
      selected: {},
      matchedSku: null,
      count: 1,
      currentPic: '',
      pics: [],
      buying: false,
      pendingBuy: false,
      loginShow: false,
    };
  },
  computed: {
    goodsTitle() {
      if (this.$getLocale() === 'en-US' && this.goods.title_en) {
        return this.goods.title_en;
      }
      return this.goods.title || '';
    },
    goodsDesc() {
      if (this.$getLocale() === 'en-US' && this.goods.desc_en) {
        return this.goods.desc_en;
      }
      return this.goods.desc || '';
    },
    goodsContent() {
      if (this.$getLocale() === 'en-US' && this.goods.content_en) {
        return this.goods.content_en;
      }
      return this.goods.content || '';
    },
    displayPrice() {
      if (this.matchedSku && this.matchedSku.price != null) {
        return (this.matchedSku.price / 100).toFixed(2);
      }
      const range = this.goods.price_range;
      if (range && range.length) {
        return range[0] === range[1] ? range[0] : `${range[0]} - ${range[1]}`;
      }
      return '0';
    },
  },
  mounted() {
    this.fetchDetail();
  },
  methods: {
    money: spectatorMoney,
    goMall() {
      this.$router.push({ path: '/spectator' });
    },
    skuFieldLabel(tree) {
      if (this.$getLocale() === 'en-US' && tree.k_en) {
        return tree.k_en;
      }
      return tree.k || '';
    },
    skuAttrLabel(attr) {
      if (this.$getLocale() === 'en-US' && attr.name_en) {
        return attr.name_en;
      }
      return attr.name || '';
    },
    fetchDetail() {
      const id = this.$route.query.id;
      if (!id) {
        Toast(this.$T('观众中心商品不存在'));
        return;
      }
      const that = this;
      that.$api.get(
        '/api/goods/goodsDetail',
        { id },
        (res) => {
          const payload = res.data || {};
          that.goods = payload.data || payload || {};
          that.pics = that.goods.pics_url || (that.goods.pic_url ? [that.goods.pic_url] : []);
          that.currentPic = that.pics[0] || '';
          document.title = that.goodsTitle || that.$T('观众中心商城标题');
          const sku = payload.sku || {};
          that.skuTree = sku.tree || [];
          that.skuList = sku.list || [];
          that.initDefaultSku();
        },
        (res) => {
          Toast((res && res.msg) || that.$T('观众中心商品不存在'));
        }
      );
    },
    initDefaultSku() {
      if (!this.skuList.length) {
        this.matchedSku = null;
        return;
      }
      if (!this.skuTree.length) {
        this.matchedSku = this.skuList[0];
        return;
      }
      const first = this.skuList[0];
      const selected = {};
      this.skuTree.forEach((tree) => {
        if (first[tree.k_s] != null) {
          selected[tree.k_s] = first[tree.k_s];
        }
      });
      this.selected = selected;
      this.resolveSku();
    },
    selectAttr(k_s, attr) {
      this.$set(this.selected, k_s, attr.id);
      if (attr.sku_pic_url) {
        this.currentPic = attr.sku_pic_url;
      }
      this.resolveSku();
    },
    resolveSku() {
      if (!this.skuTree.length) {
        this.matchedSku = this.skuList[0] || null;
        return;
      }
      const keys = this.skuTree.map((t) => t.k_s);
      const allSelected = keys.every((k) => this.selected[k] != null);
      if (!allSelected) {
        this.matchedSku = null;
        return;
      }
      this.matchedSku =
        this.skuList.find((sku) => keys.every((k) => String(sku[k]) === String(this.selected[k]))) ||
        null;
    },
    changeCount(delta) {
      this.count = Math.max(1, (Number(this.count) || 1) + delta);
    },
    normalizeCount() {
      this.count = Math.max(1, parseInt(this.count, 10) || 1);
    },
    buyNow() {
      if (this.skuTree.length && !this.matchedSku) {
        Toast(this.$T('观众中心请选择规格'));
        return;
      }
      const skuId = this.matchedSku ? this.matchedSku.id : 0;
      if (!skuId && this.skuList.length) {
        Toast(this.$T('观众中心请选择规格'));
        return;
      }
      this.normalizeCount();
      this.pendingBuy = true;
      this.ensureLoginThenBuy();
    },
    ensureLoginThenBuy() {
      const that = this;
      that.$api.post(
        '/api/index/userdata',
        {},
        () => {
          that.submitBuy();
        },
        (res) => {
          if (res && (res.code == 401 || res.code == 402)) {
            that.loginShow = true;
          } else if (!localStorage.getItem('_token') && !localStorage.getItem('_pspt')) {
            that.loginShow = true;
          } else {
            Toast((res && res.msg) || that.$T('观众中心请先登录'));
            that.loginShow = true;
          }
        }
      );
    },
    submitBuy() {
      const skuId = this.matchedSku
        ? this.matchedSku.id
        : this.skuList[0] && this.skuList[0].id;
      if (!skuId) {
        Toast(this.$T('观众中心请选择规格'));
        return;
      }
      const goods_items = [
        {
          goods_id: this.goods.id,
          sku_id: skuId,
          count: this.count,
        },
      ];
      try {
        sessionStorage.setItem(BUY_KEY, JSON.stringify(goods_items));
      } catch (e) {
        /* ignore */
      }
      this.$router.push({ path: '/spectator/order' });
    },
    loginSuccess() {
      this.loginShow = false;
      if (this.pendingBuy) {
        this.submitBuy();
      }
    },
    loginCancel() {
      this.loginShow = false;
      this.pendingBuy = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.bc-spec-detail {
  background: #f3f4f6;
  min-height: 50vh;
  padding: 32px 24px 80px;
}
.bc-spec-detail__inner {
  max-width: 1100px;
  margin: 0 auto;
}
.bc-spec-detail__back {
  display: inline-block;
  margin-bottom: 20px;
  color: #5b6478;
  font-size: 14px;
  text-decoration: none;
}
.bc-spec-detail__back:hover {
  color: #1e2842;
}
.bc-spec-detail__main {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 6px 20px rgba(30, 40, 66, 0.06);
}
.bc-spec-detail__pic {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 8px;
  background: #eef0f4;
}
.bc-spec-detail__thumbs {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.bc-spec-detail__thumb {
  width: 56px;
  height: 56px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
}
.bc-spec-detail__thumb.is-active {
  border-color: #1e2842;
}
.bc-spec-detail__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.bc-spec-detail__title {
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 700;
  color: #1e2842;
  line-height: 1.35;
}
.bc-spec-detail__desc {
  margin: 0 0 16px;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}
.bc-spec-detail__price {
  margin: 0 0 24px;
  font-size: 28px;
  font-weight: 700;
  color: #c2410c;
}
.bc-spec-detail__sku-group {
  margin-bottom: 18px;
}
.bc-spec-detail__sku-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
}
.bc-spec-detail__sku-opts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.bc-spec-detail__sku-opt {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 14px;
  cursor: pointer;
  color: #1e2842;
}
.bc-spec-detail__sku-opt.is-active {
  border-color: #1e2842;
  background: #1e2842;
  color: #fff;
}
.bc-spec-detail__qty {
  margin: 8px 0 28px;
}
.bc-spec-detail__qty-ctrl {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
}
.bc-spec-detail__qty-ctrl button {
  width: 36px;
  height: 36px;
  border: 0;
  background: #f9fafb;
  cursor: pointer;
  font-size: 18px;
}
.bc-spec-detail__qty-ctrl input {
  width: 48px;
  height: 36px;
  border: 0;
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  text-align: center;
  font-size: 14px;
}
.bc-spec-detail__buy {
  width: 100%;
  max-width: 280px;
  height: 48px;
  border: 0;
  border-radius: 8px;
  background: #c2410c;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}
.bc-spec-detail__buy:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.bc-spec-detail__buy:hover:not(:disabled) {
  background: #9a3412;
}
.bc-spec-detail__content {
  margin-top: 28px;
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 6px 20px rgba(30, 40, 66, 0.06);
}
.bc-spec-detail__content h2 {
  margin: 0 0 16px;
  font-size: 18px;
  color: #1e2842;
}
.bc-spec-detail__html {
  font-size: 14px;
  line-height: 1.7;
  color: #374151;
  overflow: hidden;
}
.bc-spec-detail__html >>> img {
  max-width: 100%;
  height: auto;
}
.bc-spec-detail__empty {
  text-align: center;
  padding: 80px 0;
  color: #6b7280;
}

@media (max-width: 800px) {
  .bc-spec-detail__main {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// SpectatorGoodsDetail.vue?edcfaa8a